import { useCallback, useEffect, useState } from 'react';
import { getTelegram, isInTelegram, alertDialog } from '../telegram';
import { getDepositInvoices, getDepositTransactions, sendDepositInvoiceLink, downloadDepositInvoice } from '../api';
import { WebBackButton } from '../components/WebNav';
import ActsSection from '../components/ActsSection';
import type { DepositAccount, DepositInvoice, DepositTransaction } from '../types';

// ============================================================================
// Блок D (депозиты): страница «Депозит» в Mini App и на сайте.
// Остаток / резерв / доступно по кофейне, кнопка «Пополнить», выставленные счета
// (повторно получить ссылку или PDF) и журнал операций. Для boss / network_manager
// показывает все доступные им точки (переключатель сверху).
// Список депозитов грузит родитель (App / WebShop) — он же открывает окно
// пополнения; операции и счета по выбранной точке страница грузит сама.
// ============================================================================

interface Props {
  onBack: () => void;
  backLabel?: string;
  deposits: DepositAccount[];
  // loading — идёт загрузка списка депозитов; loaded — список загружен хотя бы раз.
  loading: boolean;
  loaded: boolean;
  onTopUp: (coffeeShopId: string) => void;
  initialShopId?: string | null;
}

const KIND_LABEL: Record<string, string> = {
  topup: 'Пополнение',
  hold: 'Резерв под заказ',
  hold_release: 'Снятие резерва',
  debit: 'Списание за заказ',
  claim_credit: 'Возврат по претензии',
  adjustment_credit: 'Корректировка (+)',
  adjustment_debit: 'Корректировка (−)',
  refund: 'Возврат средств',
};

// Как операция меняет баланс: резервы баланс не трогают (только доступный остаток).
const KIND_SIGN: Record<string, '+' | '−' | ''> = {
  topup: '+', claim_credit: '+', adjustment_credit: '+',
  debit: '−', adjustment_debit: '−', refund: '−',
  hold: '', hold_release: '',
};

const INVOICE_LABEL: Record<string, string> = {
  issued: 'Ожидает оплаты',
  partially_paid: 'Оплачен частично',
  paid: 'Оплачен',
  cancelled: 'Аннулирован',
};

const INVOICE_COLOR: Record<string, string> = {
  issued: '#ff9500',
  partially_paid: '#ff9500',
  paid: '#34c759',
  cancelled: 'var(--tg-hint)',
};

const fmtPrice = (n: number | string) =>
  Number(n).toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2 });

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });

const btnPrimary: React.CSSProperties = {
  width: '100%', padding: '12px', border: 'none', borderRadius: 12,
  background: 'var(--tg-button)', color: 'var(--tg-button-text, #fff)',
  fontSize: 15, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
};

const btnSmall: React.CSSProperties = {
  flex: '1 1 auto', padding: '8px 10px', border: 'none', borderRadius: 10,
  background: 'var(--tg-secondary-bg)', color: 'var(--tg-button)',
  fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
};

export default function DepositPage({
  onBack, backLabel = '‹ Назад', deposits, loading, loaded, onTopUp, initialShopId,
}: Props) {
  const [shopId, setShopId] = useState<string>(initialShopId ?? '');
  // Если выбранной точки нет в списке (или она не задана) — берём первую.
  const shop = deposits.find(d => d.coffee_shop_id === shopId) ?? deposits[0] ?? null;
  const currentId = shop?.coffee_shop_id ?? '';

  const [transactions, setTransactions] = useState<DepositTransaction[]>([]);
  const [invoices, setInvoices] = useState<DepositInvoice[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  // Какая кнопка занята: `${invoiceId}:${channel}`.
  const [sending, setSending] = useState<string | null>(null);

  // Telegram: системная кнопка «Назад», главная кнопка скрыта.
  useEffect(() => {
    const tg = getTelegram();
    if (!tg) return;
    tg.BackButton.show();
    tg.BackButton.onClick(onBack);
    tg.MainButton.hide();
    return () => { tg.BackButton.offClick(onBack); tg.BackButton.hide(); };
  }, [onBack]);

  const loadDetails = useCallback(async (id: string) => {
    if (!id) { setTransactions([]); setInvoices([]); return; }
    setDetailsLoading(true);
    try {
      const [tx, inv] = await Promise.all([getDepositTransactions(id), getDepositInvoices(id)]);
      setTransactions(tx.data);
      setInvoices(inv.data);
    } catch (e) {
      alertDialog(`Не удалось загрузить операции: ${(e as Error).message}`);
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  // Перечитываем при смене точки и после обновления списка депозитов (после пополнения
  // родитель перечитывает депозиты — приходит новый массив).
  useEffect(() => { loadDetails(currentId); }, [currentId, deposits, loadDetails]);

  const send = async (inv: DepositInvoice, channel: 'telegram' | 'email') => {
    if (sending) return;
    setSending(`${inv.id}:${channel}`);
    try {
      await sendDepositInvoiceLink(inv.id, channel);
      alertDialog(channel === 'telegram'
        ? `Ссылка на счёт ${inv.number} отправлена в чат с ботом.`
        : `Ссылка на счёт ${inv.number} отправлена на ваш e-mail.`);
    } catch (e) {
      alertDialog((e as Error).message);
    } finally {
      setSending(null);
    }
  };

  const download = async (inv: DepositInvoice) => {
    if (sending) return;
    setSending(`${inv.id}:download`);
    try {
      await downloadDepositInvoice(inv.id, inv.number);
    } catch (e) {
      alertDialog((e as Error).message);
    } finally {
      setSending(null);
    }
  };

  return (
    <div className="container" style={{ padding: '16px 16px 32px' }}>
      <WebBackButton onClick={onBack} label={backLabel} />
      <h3 style={{ margin: '0 0 12px', fontSize: 18 }}>Депозит</h3>

      {loading && !loaded && <p className="hint">Загрузка…</p>}

      {loaded && deposits.length === 0 && (
        <p className="hint" style={{ lineHeight: 1.5 }}>
          Депозит пока недоступен: заявка ещё не одобрена поставщиком, у вашей кофейни не заполнено
          юрлицо или нет доступа к точке. Обратитесь к менеджеру поставщика.
        </p>
      )}

      {deposits.length > 1 && (
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span className="hint">Кофейня</span>
          <select
            value={currentId}
            onChange={e => setShopId(e.target.value)}
            style={{ width: '100%', marginTop: 4, padding: 10, borderRadius: 10, fontFamily: 'inherit', fontSize: 15 }}
          >
            {deposits.map(d => (
              <option key={d.coffee_shop_id} value={d.coffee_shop_id}>
                {d.coffee_shop_name} · {fmtPrice(d.available)}
              </option>
            ))}
          </select>
        </label>
      )}

      {shop && (
        <>
          {/* Остатки */}
          <div style={{ background: 'var(--tg-secondary-bg)', borderRadius: 14, padding: '14px 16px', marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{shop.coffee_shop_name}</div>
            {shop.legal_entity_name && (
              <div className="hint" style={{ fontSize: 13 }}>{shop.legal_entity_name}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 10 }}>
              <span>Доступно для заказов</span>
              <span style={{ fontSize: 22, fontWeight: 800, color: shop.can_order ? 'var(--tg-button)' : '#ff3b30' }}>
                {fmtPrice(shop.available)}
              </span>
            </div>
            <div className="hint" style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 13 }}>
              <span>Баланс</span><span>{fmtPrice(shop.balance)}</span>
            </div>
            <div className="hint" style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2, fontSize: 13 }}>
              <span>В резерве под заказы в работе</span><span>{fmtPrice(shop.reserved)}</span>
            </div>
            {!shop.can_order && shop.block_reason && (
              <div style={{ marginTop: 8, color: '#ff3b30', fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>
                {shop.block_reason}
              </div>
            )}
            <button onClick={() => onTopUp(shop.coffee_shop_id)} style={{ ...btnPrimary, marginTop: 12 }}>
              Пополнить депозит
            </button>
            <p className="hint" style={{ margin: '8px 0 0', fontSize: 12, lineHeight: 1.4 }}>
              Минимальный заказ {fmtPrice(shop.min_order_amount)}. Пополнение зачисляется автоматически
              после поступления денег в банк, обычно в течение 10 минут.
            </p>
          </div>

          {/* Счета на пополнение */}
          <h4 style={{ margin: '0 0 8px', fontSize: 15 }}>Счета на пополнение</h4>
          {detailsLoading && invoices.length === 0 && <p className="hint">Загрузка…</p>}
          {!detailsLoading && invoices.length === 0 && (
            <p className="hint" style={{ marginBottom: 16 }}>Счетов пока нет.</p>
          )}
          {invoices.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
              {invoices.map(inv => {
                const open = inv.status === 'issued' || inv.status === 'partially_paid';
                return (
                  <div key={inv.id} className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 15 }}>{inv.number}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: INVOICE_COLOR[inv.status] ?? 'var(--tg-hint)', whiteSpace: 'nowrap' }}>
                        {INVOICE_LABEL[inv.status] ?? inv.status}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                      <span className="hint" style={{ fontSize: 13 }}>от {fmtDate(inv.issued_at)}</span>
                      <span style={{ fontWeight: 700, color: 'var(--tg-button)' }}>{fmtPrice(inv.amount)}</span>
                    </div>
                    {inv.status === 'partially_paid' && (
                      <div className="hint" style={{ fontSize: 13, marginTop: 2 }}>оплачено {fmtPrice(inv.paid_amount)}</div>
                    )}
                    {open && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                        {isInTelegram() ? (
                          <button onClick={() => send(inv, 'telegram')} disabled={!!sending} style={{ ...btnSmall, opacity: sending ? 0.6 : 1 }}>
                            {sending === `${inv.id}:telegram` ? 'Отправляем…' : '📩 Ссылку в чат'}
                          </button>
                        ) : (
                          <button onClick={() => download(inv)} disabled={!!sending} style={{ ...btnSmall, opacity: sending ? 0.6 : 1 }}>
                            {sending === `${inv.id}:download` ? 'Загрузка…' : '📄 Скачать PDF'}
                          </button>
                        )}
                        <button onClick={() => send(inv, 'email')} disabled={!!sending} style={{ ...btnSmall, opacity: sending ? 0.6 : 1 }}>
                          {sending === `${inv.id}:email` ? 'Отправляем…' : '✉️ На e-mail'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Блок F: акт сверки по юрлицу кофейни */}
          {shop.legal_entity_id && (
            <ActsSection legalEntityId={shop.legal_entity_id} legalEntityName={shop.legal_entity_name} />
          )}

          {/* Журнал операций */}
          <h4 style={{ margin: '0 0 4px', fontSize: 15 }}>Операции</h4>
          {detailsLoading && transactions.length === 0 && <p className="hint">Загрузка…</p>}
          {!detailsLoading && transactions.length === 0 && (
            <p className="hint">Операций пока нет: депозит пополняется после оплаты счёта.</p>
          )}
          {transactions.length > 0 && (
            <div>
              {transactions.map(t => {
                const sign = KIND_SIGN[t.kind] ?? '';
                const available = Number(t.balance_after) - Number(t.reserved_after);
                const showComment = t.comment && !['hold', 'hold_release', 'debit'].includes(t.kind);
                return (
                  <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--tg-secondary-bg)' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {KIND_LABEL[t.kind] ?? t.kind}
                        {t.order_number ? ` · ${t.order_number}` : ''}
                        {t.invoice_number ? ` · ${t.invoice_number}` : ''}
                      </div>
                      <div className="hint" style={{ fontSize: 12 }}>{fmtDateTime(t.created_at)}</div>
                      {showComment && (
                        <div className="hint" style={{ fontSize: 12, lineHeight: 1.35, marginTop: 2 }}>{t.comment}</div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 700, color: sign === '+' ? '#34c759' : sign === '−' ? '#ff3b30' : 'var(--tg-hint)' }}>
                        {sign}{fmtPrice(t.amount)}
                      </div>
                      <div className="hint" style={{ fontSize: 12 }}>доступно {fmtPrice(available)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
